/**
 * "Compare within Agroforestry" — the sub-indicator comparison, client observation P5(3).
 *
 * A 2025 baseline series is one of a set. *Agroforestry — manure or compost applied to trees*
 * is 9.9 % in Burkina Faso, and the number a reader actually wants is not that one: it is
 * where it sits among the eight practices the same households were asked about. The
 * choropleth can only paint one of them, and the picker can only offer them one at a time, so
 * the comparison had nowhere to happen.
 *
 * This is that comparison, at the geography and year the panel above it is already about: one
 * horizontal bar per sibling, longest first, the mapped one marked. Clicking a bar maps that
 * sibling — the panel is about the mapped indicator, so the list re-sorts around the new one
 * and the map follows in the same click.
 *
 * ## Where the numbers come from
 *
 * The same query the panel's own headline uses, one per sibling: `?level=programme` unfiltered
 * (§E4's one number for the scope) and `?parent=<scope>&level=<its own level>` under a filter.
 * That means **the mapped sibling costs nothing** — it is already in the cache under exactly
 * this key — and every other one is a single-row payload that stays cached for the session.
 *
 * It is N requests for N siblings, which for crop yield's twenty is the largest set in the
 * catalogue. `/indicators/values/bulk` would answer them in one, and should: it is capped at
 * six ids and does not take the programme pseudo-level, so widening it is a small backend
 * change and the right follow-up (docs/NOTES.md). It is not done here because the panel is
 * only mounted for one indicator at a time, the requests are cached, and shipping the
 * comparison mattered more than shipping it in one round trip.
 *
 * **Only where the comparison is meaningful.** A sibling set is a group with more than one
 * member (`siblingsOf`); nothing outside the baseline has one, so this renders for the 58
 * baseline series and for nothing else.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueries } from '@tanstack/react-query';

import {
  fetchProgrammeValue,
  fetchValues,
  modeFor,
  queryKeys,
  type Indicator,
  type ValueMode,
} from '../../lib/api';
import { NO_VALUE, fmtNum } from '../../lib/format';
import { effectiveLevels, scopeOwnLevel, selectYear, useAppStore } from '../../lib/state';
import { leafLabel, siblingsOf } from '../sidebar/baselineGroups';

export interface SiblingBarsProps {
  /** The mapped indicator's catalogue row. */
  ind: Indicator;
  /** The whole catalogue — where the siblings are found. */
  catalogue: Indicator[];
  /** The domain colour the panel is already using, for the mapped bar. */
  color: string;
  /**
   * Whether the panel this sits in is actually on screen — default `true`.
   *
   * The sidebar collapses by leaving `#sidebar` mounted under a CSS class and `aria-hidden`
   * (`features/sidebar/Sidebar`), which is right for a pane that slides back in, and wrong
   * for a fan-out: a reader who collapses the sidebar to see the map and then moves the year
   * or the filter was still paying twenty requests a change for a list nobody can read. One
   * request for a hidden panel is a rounding error; twenty is a budget.
   */
  enabled?: boolean;
}

/** The bar of every sibling that is not the one being compared against. */
const REST = 'var(--line-2)';

/**
 * How long a year has to hold still before twenty requests go out for it.
 *
 * The §F5.10 slider is an `<input type="range">`, so a drag from 2021 to 2026 fires
 * `onChange` at every step it passes through. One indicator's own headline is one request per
 * step and always was; **this panel multiplies that by the size of the sheet**, and crop
 * yield's is twenty — a hundred requests for a drag nobody meant as five queries, against a
 * §I4 throttle of 600/min that the map's own tiles and values are also spending.
 *
 * Short enough that a reader who lands on a year sees the bars move as part of the same
 * gesture, long enough that the years passed *through* cost nothing. The mapped indicator's
 * own figure above is not debounced: it is one request, and it is the number being read.
 */
const YEAR_SETTLE_MS = 250;

/** `value`, once it has stopped changing for `ms`. Seeded with `value`, so a mount is not delayed. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    if (settled === value) return;
    const id = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(id);
  }, [value, settled, ms]);
  return settled;
}

export function SiblingBars({ ind, catalogue, color, enabled = true }: SiblingBarsProps) {
  const { t } = useTranslation();
  const country = useAppStore((s) => s.country);
  const scope = useAppStore((s) => s.scope);
  const year = useSettled(useAppStore(selectYear), YEAR_SETTLE_MS);
  const cumulative = useAppStore((s) => s.cumulative);
  const setIndicator = useAppStore((s) => s.setIndicator);

  const siblings = siblingsOf(ind, catalogue);
  // Hooks cannot be conditional, and `useQueries` takes an array — so an empty sibling set is
  // an empty array of queries rather than an early return above this line.
  const results = useQueries({
    queries: siblings.map((sib) => {
      const mode: ValueMode = modeFor(cumulative, sib);
      return scope
        ? {
            queryKey: queryKeys.values(
              sib.id, scopeOwnLevel(scope), year, country, mode, scope.unitId,
            ),
            queryFn: () =>
              fetchValues(sib.id, scopeOwnLevel(scope), year, country, mode, scope.unitId),
            enabled,
          }
        : {
            queryKey: queryKeys.programmeValue(sib.id, year, country, mode),
            queryFn: () => fetchProgrammeValue(sib.id, year, country, mode),
            enabled,
          };
    }),
  });

  if (siblings.length < 2) return null;

  const rows = siblings
    .map((sib, i) => ({
      ind: sib,
      label: leafLabel(sib.name),
      value: results[i]?.data?.values[0]?.value ?? null,
    }))
    // Longest first: the question a comparison answers is "which of these is biggest", and a
    // list in catalogue order makes the reader do the sorting by eye. Unreported siblings sink
    // to the bottom rather than being dropped — that one practice has no estimate here is part
    // of the picture, and a silently shorter list would hide it.
    .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));

  const largest = rows.reduce((best, r) => Math.max(best, r.value ?? 0), 0);
  const reported = rows.filter((r) => r.value !== null).length;

  return (
    <section className="ins-block" data-insblock="siblings" data-siblings={ind.group_id ?? ''}>
      {/* The 16 Sep re-skin: this block shares the panel's grammar rather than carrying the
          indicator panel's own 11 px heading — one title size for every block on the page. */}
      <h4 className="ins-title">
        {t('analytics.siblings', { group: ind.group_label ?? '' })}
      </h4>
      {reported === 0 ? (
        <p className="ins-note" data-sibempty="">
          {t('analytics.siblingsNone')}
        </p>
      ) : (
        <ul className="sib-list">
          {rows.map((row) => {
            const mapped = row.ind.id === ind.id;
            // A bar of zero width is invisible, and "0 %" is a measurement, not an absence —
            // so a reported zero keeps a hairline and only a `null` has no bar at all.
            const pct =
              row.value === null || largest <= 0
                ? 0
                : Math.max(1.5, (row.value / largest) * 100);
            return (
              <li key={row.ind.id} className={`sib-row${mapped ? ' on' : ''}`}>
                <button
                  type="button"
                  className="sib-btn"
                  data-sib={row.ind.id}
                  aria-current={mapped ? 'true' : undefined}
                  disabled={mapped || !row.ind.active}
                  title={
                    mapped
                      ? (row.ind.full_name ?? row.ind.name)
                      : t('analytics.siblingMap', { name: row.ind.name })
                  }
                  onClick={() =>
                    setIndicator(
                      row.ind.id,
                      row.ind.has_watershed_values,
                      effectiveLevels(row.ind),
                    )
                  }
                >
                  <span className="sib-name">{row.label}</span>
                  <span className="sib-track">
                    <span
                      className="sib-fill"
                      style={{ width: `${pct}%`, background: mapped ? color : REST }}
                    />
                  </span>
                  <span className="sib-val">
                    {row.value === null ? NO_VALUE : fmtNum(row.value, row.ind.fmt)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default SiblingBars;
